import React, { useEffect, useState } from "react";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography
} from "@mui/material";

import { PIPELINE_CARD_BASE_CHARACTERISTICS, PIPELINE_MAX_CARD_CHARACTERISTICS } from "../../../constants/deal";
import { GroupingStageMeta } from "../GroupingViewContext";

interface Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** The Card details LIST — the checkbox rows currently shown. */
  cardOptionsList: string[];
  /** All characteristic names that can be added to the list (e.g. account characteristics + "Contact"). */
  availableCharacteristics: string[];
  /** Currently checked characteristics (subset of the list). */
  cardCharacteristics: string[];
  hiddenStageIds: number[];
  stages: GroupingStageMeta[];
  onApply: (cardCharacteristics: string[], cardOptionsList: string[], hiddenStageIds: number[]) => void;
}

type PropertiesTab = "card" | "stages";

const checkboxSx = { p: "4px", color: "#bac7ce", "&.Mui-checked": { color: "#0a3142" } } as const;

interface TabButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

const TabButton: React.FC<TabButtonProps> = ({ label, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      cursor: "pointer",
      pt: "2px",
      pb: "6px",
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "16px",
      whiteSpace: "nowrap",
      color: active ? "#071f35" : "#496682",
      borderBottom: active ? "2px solid #071f35" : "2px solid transparent",
      transition: "color 0.15s linear"
    }}
  >
    {label}
  </Box>
);

export const PipelinePropertiesPopup: React.FC<Props> = ({
  anchorEl,
  open,
  onClose,
  cardOptionsList,
  availableCharacteristics,
  cardCharacteristics,
  hiddenStageIds,
  stages,
  onApply
}) => {
  const [tab, setTab] = useState<PropertiesTab>("card");
  const [draftOptions, setDraftOptions] = useState<string[]>(cardOptionsList);
  const [draftSelected, setDraftSelected] = useState<string[]>(cardCharacteristics);
  const [draftHidden, setDraftHidden] = useState<number[]>(hiddenStageIds);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const [addSearch, setAddSearch] = useState("");

  // Re-sync drafts each time the popup opens.
  useEffect(() => {
    if (open) {
      setDraftOptions(cardOptionsList);
      setDraftSelected(cardCharacteristics);
      setDraftHidden(hiddenStageIds);
      setTab("card");
    }
  }, [open, cardOptionsList, cardCharacteristics, hiddenStageIds]);

  const atLimit = draftSelected.length >= PIPELINE_MAX_CARD_CHARACTERISTICS;
  const addableCharacteristics = availableCharacteristics.filter((name) => !draftOptions.includes(name));
  const filteredAddable = addableCharacteristics.filter((name) =>
    name.toLowerCase().includes(addSearch.trim().toLowerCase())
  );

  const openAddPicker = (event: React.MouseEvent<HTMLElement>) => {
    setAddSearch("");
    setAddAnchor(event.currentTarget);
  };

  const toggleSelected = (name: string) => {
    setDraftSelected((prev) => (prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]));
  };

  // Add to the list and keep the picker open (with the search reset) for quick multi-add.
  const addCharacteristic = (name: string) => {
    setDraftOptions((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setAddSearch("");
  };

  // Only user-added (non-base) characteristics can be removed from the list.
  const removeOption = (name: string) => {
    setDraftOptions((prev) => prev.filter((item) => item !== name));
    setDraftSelected((prev) => prev.filter((item) => item !== name));
  };

  const toggleStage = (id: number) => {
    setDraftHidden((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const handleApply = () => {
    // Keep only checked names that still exist in the list.
    onApply(
      draftSelected.filter((name) => draftOptions.includes(name)),
      draftOptions,
      draftHidden
    );
    onClose();
  };

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{ paper: { sx: { width: 380, borderRadius: "12px", p: "24px" } } }}
    >
      <Typography sx={{ fontSize: "18px", fontWeight: "400", color: "#0a3142", mb: "20px" }}>Properties</Typography>

      <Stack direction="row" gap="16px" alignItems="stretch" sx={{ mb: "10px" }}>
        <TabButton label="Card details" active={tab === "card"} onClick={() => setTab("card")} />
        <Box sx={{ width: "1px", bgcolor: "#dbe8f5", mb: "6px" }} />
        <TabButton label="Deal Stages" active={tab === "stages"} onClick={() => setTab("stages")} />
      </Stack>

      {tab === "card" ? (
        <Box>
          <Typography sx={{ fontSize: "12px", color: "#5d7782", mb: "12px" }}>
            Up to {PIPELINE_MAX_CARD_CHARACTERISTICS} items can be used in the card
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: "10px",
              maxHeight: "200px",
              overflow: "auto"
            }}
          >
            {draftOptions.map((option) => {
              const checked = draftSelected.includes(option);
              const removable = !PIPELINE_CARD_BASE_CHARACTERISTICS.includes(option);
              return (
                <Stack key={option} direction="row" alignItems="center" sx={{ height: "34px", minWidth: 0 }}>
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={!checked && atLimit}
                    onChange={() => toggleSelected(option)}
                    sx={checkboxSx}
                  />
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#0a3142",
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {option}
                  </Typography>
                  {removable && (
                    <IconButton onClick={() => removeOption(option)} sx={{ p: "2px", width: "20px", height: "20px" }}>
                      <CloseIcon sx={{ fontSize: "14px", color: "#92a7bc" }} />
                    </IconButton>
                  )}
                </Stack>
              );
            })}
          </Box>

          <Button
            startIcon={<AddCircleOutlineIcon sx={{ fontSize: "18px" }} />}
            disabled={!addableCharacteristics.length}
            onClick={openAddPicker}
            sx={{
              mt: "8px",
              textTransform: "none",
              fontSize: "13px",
              color: "#0a3142",
              bgcolor: "#0a31420a",
              borderRadius: "20px",
              px: "12px",
              "&:hover": { bgcolor: "#0a314214" }
            }}
          >
            Add Characteristic
          </Button>
          <Popover
            anchorEl={addAnchor}
            open={Boolean(addAnchor)}
            onClose={() => setAddAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{ paper: { sx: { width: 260, mt: "4px", borderRadius: "10px", p: "8px" } } }}
          >
            <TextField
              autoFocus
              fullWidth
              placeholder="Search characteristics"
              value={addSearch}
              onChange={(e) => setAddSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#92a7bc" }} />
                  </InputAdornment>
                )
              }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px", height: "34px", fontSize: "13px" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe8f5" }
              }}
            />
            <Box sx={{ maxHeight: 200, overflow: "auto", mt: "6px" }}>
              {filteredAddable.length ? (
                filteredAddable.map((name) => (
                  <MenuItem
                    key={name}
                    onClick={() => addCharacteristic(name)}
                    sx={{
                      fontSize: "13px",
                      color: "#0a3142",
                      borderRadius: "6px",
                      px: "8px",
                      minHeight: "32px",
                      "&:hover": { bgcolor: "#0a31420a" }
                    }}
                  >
                    {name}
                  </MenuItem>
                ))
              ) : (
                <Typography sx={{ fontSize: "13px", color: "#92a7bc", p: "8px" }}>No characteristics found</Typography>
              )}
            </Box>
          </Popover>
        </Box>
      ) : (
        <Stack sx={{ maxHeight: "200px", overflow: "auto" }}>
          {stages.length ? (
            stages.map((stage) => {
              const visible = !draftHidden.includes(stage.id);
              return (
                <Stack key={stage.id} direction="row" alignItems="center" sx={{ height: "34px", minWidth: 0 }}>
                  <Checkbox size="small" checked={visible} onChange={() => toggleStage(stage.id)} sx={checkboxSx} />
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#0a3142",
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {stage.title}
                  </Typography>
                </Stack>
              );
            })
          ) : (
            <Typography sx={{ fontSize: "13px", color: "#5d7782" }}>No stages available</Typography>
          )}
        </Stack>
      )}

      <Stack direction="row" justifyContent="flex-end" gap="8px" sx={{ mt: "20px" }}>
        <Button variant="outlined" onClick={onClose} sx={{ textTransform: "none", borderRadius: "20px" }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleApply} sx={{ textTransform: "none", borderRadius: "20px" }}>
          Apply
        </Button>
      </Stack>
    </Popover>
  );
};
